import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { usePlanBuilder } from '../context/PlanBuilderContext';
import { DownloadIcon, CheckIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, CalendarIcon, RefreshCwIcon } from 'lucide-react';
import Button from '../../../../../ui/canon/Button';
import SearchBar from '../../../../../ui/canon/SearchBar';
import EditAnswerModal from '../dialogs/EditAnswerModal';

const ViewPlanPanel = ({ onClose }) => {
  const intl = useIntl();
  const { progress, exportPDF, isUpdatingPlan, highlightedQuestionId, editAnswer, submitting } = usePlanBuilder();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const questionRefs = useRef({});
  
  // Edit modal state
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const planTemplate = progress?.planTemplate;

  // Auto-expand and scroll to highlighted question
  useEffect(() => {
    if (highlightedQuestionId && planTemplate) {
      // Find which module and section contains this question
      for (const module of planTemplate.planModules) {
        for (const section of module.planSections) {
          const question = section.planQuestions?.find(q => q.id === highlightedQuestionId);
          if (question) {
            // Expand the module and section
            setExpandedModules(prev => ({ ...prev, [module.id]: true }));
            setExpandedSections(prev => ({ ...prev, [section.id]: true }));

            // Scroll to the question after a brief delay to allow expansion
            setTimeout(() => {
              const questionEl = questionRefs.current[highlightedQuestionId];
              if (questionEl) {
                questionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            return;
          }
        }
      }
    }
  }, [highlightedQuestionId, planTemplate]);

  if (!planTemplate) {
    return (
      <div className="flex flex-col h-full bg-succession-bg-primary w-full items-center justify-center">
        <div className="text-center space-y-2">
          <div className="w-8 h-8 border-2 border-succession-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.view_plan_loading' })}</p>
        </div>
      </div>
    );
  }

  const { userPlanResponses } = progress;

  // Create answer map for quick lookup
  const answerMap = {};
  if (userPlanResponses) {
    userPlanResponses.forEach(r => {
      answerMap[r.planQuestionId] = r;
    });
  }

  const hasAnswer = (questionId) => !!answerMap[questionId];
  const getAnswer = (questionId) => answerMap[questionId]?.answer;

  // Find a question by ID across all modules/sections
  const findQuestionById = (questionId) => {
    for (const module of planTemplate.planModules) {
      for (const section of module.planSections) {
        const question = section.planQuestions?.find(q => q.id === questionId);
        if (question) {
          return { ...question, planSection: section, planModule: module };
        }
      }
    }
    return null;
  };

  // Handle edit button click
  const handleEditQuestion = (questionId) => {
    const question = findQuestionById(questionId);
    if (question) {
      setEditingQuestion(question);
      setEditModalOpen(true);
    }
  };

  // Handle save from edit modal
  const handleSaveEdit = async (questionId, newAnswer) => {
    try {
      const success = await editAnswer(questionId, newAnswer);
      if (success) {
        setEditModalOpen(false);
        setEditingQuestion(null);
      }
    } catch (error) {
      console.error('Error saving edited answer:', error);
    }
  };

  // Handle modal close
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingQuestion(null);
  };

  // Calculate total questions dynamically if not available directly
  // This ensures "12 of 0" becomes "12 of X"
  const totalQuestions = planTemplate.planModules.reduce((acc, mod) => {
    return acc + mod.planSections.reduce((secAcc, sec) => {
        return secAcc + (sec.planQuestions?.length || 0);
    }, 0);
  }, 0);

  // Toggle helpers
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Filtering Logic
  const matchesSearch = (section) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const sectionMatches = section.title.toLowerCase().includes(query);
    const questionMatches = section.planQuestions.some(q => 
      q.questionText.toLowerCase().includes(query) || 
      (answerMap[q.id]?.answer?.formatted || "").toLowerCase().includes(query)
    );
    return sectionMatches || questionMatches;
  };

  // Progress Calculation
  const calculateCompleteness = (items) => {
    const total = items.length;
    const answered = items.filter(item => hasAnswer(item.id)).length;
    const percentage = total === 0 ? 0 : Math.round((answered / total) * 100);
    
    let status = "not-started";
    if (percentage === 100) status = "complete";
    else if (percentage > 0) status = "partial";

    return { answered, total, percentage, status };
  };

  const renderAnswerWidget = (widgetType, answer) => {
    const value = answer?.value;
    const formatted = answer?.formatted;

    if (!value) return <span className="text-succession-text-muted italic">{intl.formatMessage({ id: 'succession.plans.view_plan_not_answered' })}</span>;

    switch(widgetType) {
      case 'yesno':
      case 'yes-no':
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value === 'yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {value === 'yes' ? <CheckIcon className="w-3 h-3 mr-1" /> : null}
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      case 'multiple-choice':
        const mcValues = Array.isArray(value) ? value : [value];
        return (
          <div className="flex flex-wrap gap-2">
            {mcValues.map((v, i) => (
              <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                {v}
              </span>
            ))}
          </div>
        );
      case 'checklist':
        const checklistValues = Array.isArray(value) ? value : [value];
        return (
          <div className="flex flex-wrap gap-2">
            {checklistValues.map((v, i) => (
              <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                <CheckIcon className="w-3 h-3 mr-1" />
                {v}
              </span>
            ))}
          </div>
        );
      case 'contact':
        // Handle contact object with name, role, email, phone fields
        if (typeof value === 'object') {
          const parts = [];
          if (value.name) parts.push(value.name);
          if (value.role || value.title) parts.push(value.role || value.title);
          if (value.email) parts.push(value.email);
          if (value.phone) parts.push(value.phone);
          return (
            <div className="bg-succession-bg-secondary rounded-lg p-3 border border-succession-border">
              {parts.map((part, i) => (
                <p key={i} className={`text-sm ${i === 0 ? 'font-medium text-succession-text-primary' : 'text-succession-text-secondary'}`}>
                  {part}
                </p>
              ))}
            </div>
          );
        }
        return <p className="text-sm text-succession-text-primary">{formatted || String(value)}</p>;
      case 'currency':
      case 'number':
        return <span className="font-mono font-medium text-succession-text-primary">{formatted || value}</span>;
      case 'percentage':
        return <span className="font-mono font-medium text-succession-text-primary">{value}%</span>;
      case 'date':
        return (
          <span className="inline-flex items-center text-sm text-succession-text-primary">
            <CalendarIcon className="w-4 h-4 mr-2 text-succession-text-secondary" />
            {formatted || value}
          </span>
        );
      case 'rating':
        return <span className="font-medium text-succession-text-primary">{value}/6</span>;
      default:
        // Ensure we never try to render an object directly
        const displayValue = typeof value === 'object' ? JSON.stringify(value) : (formatted || value);
        return <p className="text-sm text-succession-text-primary whitespace-pre-wrap">{displayValue}</p>;
    }
  };

  return (
    <>
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .content-darken {
          filter: brightness(0.5);
          transition: filter 0.3s ease-out;
        }
        @keyframes highlightFade {
          0% { 
            background-color: rgba(var(--succession-primary-rgb, 59, 130, 246), 0.3);
            box-shadow: 0 0 0 3px rgba(var(--succession-primary-rgb, 59, 130, 246), 0.4);
          }
          100% { 
            background-color: transparent;
            box-shadow: none;
          }
        }
        .highlight-answer {
          animation: highlightFade 3s ease-out forwards;
          border-radius: 8px;
        }
      `}</style>
      <div className="flex flex-col h-full bg-succession-bg-primary w-full relative">
        {/* Updating Overlay Animation - Positioned above blurred content */}
        {isUpdatingPlan && (
          <div className="absolute inset-0 z-50 flex items-center justify-center animate-fade-in" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="flex flex-col items-center gap-5 p-10 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-zinc-700">
              {/* Animated ring - larger and more prominent */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full border-4 border-succession-primary/30"></div>
                <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-transparent border-t-succession-primary animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <RefreshCwIcon className="h-8 w-8 text-succession-primary animate-pulse" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <p className="text-base font-bold text-gray-900 dark:text-white">{intl.formatMessage({ id: 'succession.plans.view_plan_updating' })}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{intl.formatMessage({ id: 'succession.plans.view_plan_adding_response' })}</p>
              </div>
              {/* Progress dots - larger */}
              <div className="flex gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-succession-primary animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2.5 h-2.5 rounded-full bg-succession-primary animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2.5 h-2.5 rounded-full bg-succession-primary animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable content - darkens when updating */}
        <div className={`flex-1 overflow-y-auto ${isUpdatingPlan ? 'content-darken' : ''}`}>

      {/* Document Sections Header */}
      <div className="px-6 py-3 bg-succession-bg-primary">
        <div className="flex items-center justify-end h-8">
          <div className="w-64">
            <SearchBar
              placeholder={intl.formatMessage({ id: 'succession.plans.view_plan_search_placeholder' })}
              value={searchQuery}
              onChange={setSearchQuery}
              size="sm"
              variant="outline"
            />
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="flex-1 px-6 py-6 space-y-4">
        {planTemplate.planModules.map((module, moduleIdx) => {
          // Determine completeness for module (aggregating all questions)
          const allQuestions = module.planSections.flatMap(s => s.planQuestions);
          const moduleStats = calculateCompleteness(allQuestions);
          const isModuleOpen = expandedModules[module.id];

          // Filter sections
          const visibleSections = module.planSections.filter(matchesSearch);
          if (visibleSections.length === 0) return null;

          return (
            <div key={module.id} className="border border-succession-border rounded-lg overflow-hidden bg-succession-bg-primary">
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-succession-bg-secondary transition-colors"
              >
                <div className="flex items-center gap-3">
                  {moduleStats.status === "complete" ? (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                  ) : moduleStats.status === "partial" ? (
                    <AlertCircleIcon className="h-5 w-5 text-orange-500" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                  )}
                  
                  <div className="text-left">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.view_plan_module_label' }, { number: moduleIdx + 1 })}</span>
                      <span className="text-xs px-1.5 py-0 bg-succession-bg-secondary rounded text-succession-text-secondary border border-succession-border">
                        {intl.formatMessage({ id: 'succession.plans.view_plan_percent_complete' }, { percent: moduleStats.percentage })}
                      </span>
                    </div>
                    <h2 className="font-bold text-base text-succession-text-primary">{module.title}</h2>
                    <p className="text-xs text-succession-text-secondary mt-0.5">{module.description}</p>
                  </div>
                </div>
                {isModuleOpen ? <ChevronUpIcon className="h-5 w-5 text-succession-text-muted" /> : <ChevronDownIcon className="h-5 w-5 text-succession-text-muted" />}
              </button>

              {isModuleOpen && (
                <div className="border-t border-succession-border bg-succession-bg-secondary/30 p-4 space-y-3">
                  {visibleSections.map((section, sectionIdx) => {
                    const sectionStats = calculateCompleteness(section.planQuestions);
                    const isSectionOpen = expandedSections[section.id];

                    return (
                      <div key={section.id} className="border border-succession-border rounded-lg overflow-hidden bg-succession-bg-primary">
                        <button
                          onClick={() => toggleSection(section.id)}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-succession-bg-secondary transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {sectionStats.status === "complete" ? (
                              <CheckIcon className="h-4 w-4 text-green-600" />
                            ) : sectionStats.status === "partial" ? (
                              <AlertCircleIcon className="h-4 w-4 text-orange-500" />
                            ) : (
                              <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                            )}
                            
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.view_plan_section_label' }, { module: moduleIdx + 1, section: sectionIdx + 1 })}</span>
                                <span className={`text-xs px-1.5 py-0 rounded border ${
                                  sectionStats.status === "complete" ? "bg-green-50 text-green-700 border-green-200" :
                                  sectionStats.status === "partial" ? "bg-orange-50 text-orange-700 border-orange-200" :
                                  "bg-gray-50 text-gray-600 border-gray-200"
                                }`}>
                                  {sectionStats.status === "complete" ? intl.formatMessage({ id: 'succession.plans.view_plan_status_complete' }) : sectionStats.status === "partial" ? intl.formatMessage({ id: 'succession.plans.view_plan_status_partial' }) : intl.formatMessage({ id: 'succession.plans.view_plan_status_not_started' })}
                                </span>
                              </div>
                              <h3 className="font-semibold text-sm mt-0.5 text-succession-text-primary">{section.title}</h3>
                            </div>
                          </div>
                          {isSectionOpen ? <ChevronUpIcon className="h-4 w-4 text-succession-text-muted" /> : <ChevronDownIcon className="h-4 w-4 text-succession-text-muted" />}
                        </button>

                        {isSectionOpen && (
                          <div className="border-t border-succession-border bg-succession-bg-secondary/50 px-4 py-4 space-y-4">
                            {section.planQuestions.map(question => {
                              const answer = answerMap[question.id];
                              const answered = !!answer;
                              const isHighlighted = highlightedQuestionId === question.id;

                              return (
                                <div 
                                  key={question.id} 
                                  ref={el => questionRefs.current[question.id] = el}
                                  className={`bg-succession-bg-primary rounded-lg p-4 space-y-2 border border-succession-border ${isHighlighted ? 'highlight-answer' : ''}`}
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <p className="text-sm font-medium text-succession-text-primary flex-1">{question.questionText}</p>
                                    {/* Only show Edit button for answered questions */}
                                    {answered && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditQuestion(question.id)}
                                        className="gap-1.5 flex-shrink-0 h-8 text-xs"
                                      >
                                        <EditIcon className="h-3.5 w-3.5" />
                                        {intl.formatMessage({ id: 'succession.plans.view_plan_edit_button' })}
                                      </Button>
                                    )}
                                  </div>
                                  <div className="pl-0">
                                    {answered ? (
                                      <div className="text-succession-text-primary">
                                        {renderAnswerWidget(question.widgetType, answer.answer)}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-succession-text-muted italic">{intl.formatMessage({ id: 'succession.plans.view_plan_not_answered' })}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
        </div>
      </div>

      {/* Edit Answer Modal */}
      <EditAnswerModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        question={editingQuestion}
        currentAnswer={editingQuestion ? getAnswer(editingQuestion.id) : null}
        onSave={handleSaveEdit}
        saving={submitting}
      />
    </>
  );
};

export default ViewPlanPanel;
